/**
 * Questionnaire — interactive multi-step financial snapshot.
 * Route: /q/:campaignSlug/:leadId
 *
 * Design system — matches LandingPage (default):
 *   background:      #fafaf9 (brand.warmWhite)
 *   surface:         #ffffff
 *   primary/CTA:     #d97706 (brand.amber)
 *   primaryHover:    #f59e0b (brand.amberHover)
 *   text:            #292524 (brand.charcoal)
 *   textMuted:       stone-500 / stone-600
 *   card:            rounded-3xl shadow-[0_12px_40px_rgba(28,25,23,0.08)] border border-black/5
 *   eyebrow:         bg-amber-50 text-brand-amber rounded-full text-xs font-bold
 *   CTA:             rounded-full h-14 bg-brand-amber text-white with pulse-cta
 */
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, Check, MessageCircle, PhoneCall, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

import api from "@/lib/api";
import { BRAND_LOGO } from "@/lib/assets";

/* ==========================================================================
 * Small reusable UI primitives — mirror LandingPage tokens
 * ========================================================================== */
const COLORS = {
  bg: "#fafaf9",             // brand.warmWhite
  surface: "#ffffff",
  primary: "#d97706",        // brand.amber
  primaryHover: "#f59e0b",   // brand.amberHover
  text: "#292524",           // brand.charcoal
  textMuted: "#78716c",      // stone-500
  borderSoft: "rgba(0,0,0,0.06)", // border-black/5
  cardShadow: "0 12px 40px rgba(28,25,23,0.08)",
  positiveSoft: "#ecfdf5",   // emerald-50 — gentle positive
  attentionSoft: "#fef3c7",  // amber-100 — gentle attention
  neutralSoft: "#f5f5f4",    // stone-100 — neutral
  amberBadgeBg: "#fef3c7",   // amber-100
  amberBadgeText: "#d97706", // brand.amber
};

function ProgressBar({ current, total }) {
  const pct = Math.max(0, Math.min(100, Math.round((current / total) * 100)));
  return (
    <div
      className="w-full h-1.5 rounded-full overflow-hidden bg-stone-200"
      data-testid="questionnaire-progress"
    >
      <motion.div
        initial={{ width: 0 }}
        animate={{ width: `${pct}%` }}
        transition={{ duration: 0.5, ease: "easeOut" }}
        className="h-full rounded-full bg-brand-amber"
      />
    </div>
  );
}

function ChoiceCard({ label, selected, onClick, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`w-full text-right rounded-2xl border-2 px-6 py-5 text-lg font-medium transition-all ${
        selected
          ? "border-brand-amber bg-amber-50/70 shadow-[0_4px_16px_rgba(217,119,6,0.12)]"
          : "border-black/5 bg-white hover:border-stone-300 hover:bg-stone-50"
      }`}
      style={{ color: COLORS.text, minHeight: 56 }}
    >
      <div className="flex items-center gap-3">
        <span
          className={`inline-flex items-center justify-center w-6 h-6 rounded-full border-2 flex-shrink-0 ${
            selected ? "border-brand-amber bg-brand-amber" : "border-stone-300 bg-transparent"
          }`}
        >
          {selected && <Check className="w-4 h-4 text-white" strokeWidth={3} />}
        </span>
        <span>{label}</span>
      </div>
    </button>
  );
}

function MoneyInput({ value, onChange, placeholder, testId, disabled = false }) {
  const formatted = value === "" || value === null || value === undefined
    ? ""
    : Number(value).toLocaleString("he-IL");

  const handleChange = (e) => {
    const raw = e.target.value.replace(/[^\d]/g, "");
    if (!raw) return onChange("");
    onChange(parseInt(raw, 10));
  };

  return (
    <div className="relative" style={{ opacity: disabled ? 0.55 : 1 }}>
      <input
        type="text"
        inputMode="numeric"
        dir="ltr"
        value={formatted}
        onChange={handleChange}
        placeholder={placeholder}
        disabled={disabled}
        data-testid={testId}
        className="w-full h-16 rounded-2xl border-2 border-black/5 bg-white px-14 pl-6 text-2xl font-bold outline-none transition-colors text-right focus-visible:ring-2 focus-visible:ring-brand-amber focus:border-brand-amber"
        style={{ color: COLORS.text }}
      />
      <span
        className="absolute right-5 top-1/2 -translate-y-1/2 text-2xl font-bold"
        style={{ color: COLORS.textMuted }}
      >
        ₪
      </span>
    </div>
  );
}

function FeedbackBox({ tone = "neutral", children, testId }) {
  const styles = {
    positive: "bg-emerald-50 border-emerald-200/60 text-emerald-900",
    attention: "bg-amber-50 border-amber-200/60 text-amber-900",
    neutral: "bg-stone-100 border-stone-200/60 text-stone-800",
  }[tone];
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35 }}
      className={`rounded-2xl px-5 py-4 text-base leading-relaxed border ${styles}`}
      data-testid={testId}
    >
      {children}
    </motion.div>
  );
}

function PrimaryButton({ children, onClick, disabled, testId, block = true, pulse = false }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      data-testid={testId}
      className={`${block ? "w-full" : ""} rounded-full px-8 h-14 text-base font-bold text-white transition-all active:scale-95 ${
        disabled
          ? "bg-stone-300 cursor-not-allowed"
          : `bg-brand-amber hover:bg-brand-amberHover ${pulse ? "pulse-cta" : ""}`
      }`}
    >
      {children}
    </button>
  );
}

function SecondaryButton({ children, onClick, disabled, testId, block = true }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      data-testid={testId}
      className={`${block ? "w-full" : ""} rounded-full px-8 h-14 text-base font-bold transition-colors bg-white border-2 border-brand-charcoal text-brand-charcoal hover:bg-stone-50`}
    >
      {children}
    </button>
  );
}

function BackButton({ onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid="questionnaire-back-btn"
      className="inline-flex items-center gap-1.5 text-sm font-semibold text-stone-500 hover:text-stone-700 transition-colors"
    >
      <ArrowLeft className="w-4 h-4 rotate-180" />
      חזרה לשאלה הקודמת
    </button>
  );
}

function Eyebrow({ children }) {
  return (
    <span className="inline-block px-3 py-1 rounded-full bg-amber-50 text-brand-amber font-bold text-xs sm:text-sm">
      {children}
    </span>
  );
}

/* ==========================================================================
 * Questionnaire step definitions & logic
 * ==========================================================================
 * Steps (dynamic — steps that don't apply are skipped):
 *   1. Intro
 *   2. hasProperty
 *   3. propertyValue        (only if hasProperty)
 *   4. mortgage             (only if hasProperty; two fields + no-mortgage checkbox)
 *   5. otherLoans           (with no-loans checkbox)
 *   6. creditHistory
 *   7. monthlyIncome        (with dynamic ratio feedback)
 *   8. Transition
 *   9. Loading
 *  10. Result
 */

const STEPS = {
  INTRO: "intro",
  HAS_PROPERTY: "has_property",
  PROPERTY_VALUE: "property_value",
  MORTGAGE: "mortgage",
  OTHER_LOANS: "other_loans",
  CREDIT_HISTORY: "credit_history",
  INCOME: "income",
  TRANSITION: "transition",
  LOADING: "loading",
  RESULT: "result",
};

function buildStepOrder(hasProperty) {
  const order = [STEPS.INTRO, STEPS.HAS_PROPERTY];
  if (hasProperty) {
    order.push(STEPS.PROPERTY_VALUE, STEPS.MORTGAGE);
  }
  order.push(STEPS.OTHER_LOANS, STEPS.CREDIT_HISTORY, STEPS.INCOME, STEPS.TRANSITION, STEPS.LOADING, STEPS.RESULT);
  return order;
}

function tierFor(ratio, creditHistory) {
  if (ratio > 45 || creditHistory === "complex") return "needs_review";
  if (ratio > 35) return "heavy";
  if (ratio > 25) return "tight";
  return "breathing";
}

const RESULT_CONTENT = {
  breathing: {
    title: "יש לכם מרחב נשימה יחסי",
    body: `לפי הנתונים שמילאתם, חלק מההכנסה שלכם כבר מופנה להחזרים, אבל כרגע נראה שנשאר מרחב מסוים לתמרון.

זה לא אומר שהכול בהכרח מסודר.
יכול להיות שאתם עדיין משלמים ריביות גבוהות, מחזיקים כמה הלוואות במקביל או מרגישים שבסוף החודש נשאר פחות ממה שציפיתם.`,
    checklistTitle: "מה כדאי לבדוק עכשיו?",
    checklist: [
      "האם אפשר לצמצם ריביות או כפילויות",
      "האם קיימים החזרים שכבר אינם משרתים אתכם",
      "האם המבנה הנוכחי מתאים למטרות שלכם בשנים הקרובות",
    ],
    summary: "לא נדלקה כאן נורה אדומה מיידית, אבל בהחלט יכול להיות שיש מקום להפוך את הכסף שלכם ליעיל יותר.",
  },
  tight: {
    title: "חלק משמעותי מההכנסה שלכם כבר תפוס",
    body: `לפי הנתונים שמילאתם, יותר מרבע מההכנסה החודשית של משק הבית כבר מופנה למשכנתא ולהלוואות.

זה לא בהכרח אומר שמשהו לא תקין.
אבל זה כן מסביר למה גם כשנכנסת משכורת טובה, לפעמים מרגיש שאין מספיק אוויר עד סוף החודש.`,
    checklistTitle: "מה כדאי לבדוק עכשיו?",
    checklist: [
      "כמה הלוואות שונות אתם משלמים במקביל",
      "אילו ריביות מסתתרות בתוך ההחזרים",
      "האם קיימת דרך לארגן את ההתחייבויות בצורה נוחה יותר",
      "האם נשאר מספיק מקום לחיסכון ולהוצאות בלתי צפויות",
    ],
    summary: "יש כאן מקום לבדיקה. לא כדי לקחת מיד עוד הלוואה, אלא כדי להבין אם אפשר לבנות את התמונה אחרת.",
  },
  heavy: {
    title: "ההחזרים תופסים נתח גדול מהחודש שלכם",
    body: `לפי הנתונים שמילאתם, חלק גדול מההכנסה החודשית כבר מיועד להחזרים עוד לפני ששילמתם על קניות, ילדים, רכב, חשבונות והחיים עצמם.

במצב כזה, גם הכנסה טובה יכולה להרגיש כאילו היא נעלמת מהר מדי.
זו לא עדות לכישלון או לחוסר אחריות.
לפעמים זו פשוט תוצאה של כמה החלטות, פריסות והלוואות שהצטברו למערכת שכבר קשה לראות מבחוץ.`,
    checklistTitle: "מה כדאי לבדוק עכשיו?",
    checklist: [
      "אילו התחייבויות מכבידות יותר מהאחרות",
      "מה העלות הכוללת של כל הלוואה, ולא רק ההחזר החודשי",
      "האם קיים פער משמעותי בין שווי הנכס ליתרת המשכנתא",
      "מה יכול להקל על השוטף בלי ליצור בעיה חדשה",
    ],
    summary: "כדאי לבדוק את התמונה בצורה אישית ומסודרת, לפני שמוסיפים עוד פתרון זמני ללופ הקיים.",
  },
  needs_review: {
    title: "כאן שאלון קצר כבר לא מספיק",
    body: `לפי הנתונים שמילאתם, חלק גדול מאוד מההכנסה שלכם מוקצה להחזרים, או שקיימים בתמונה נתונים שדורשים הסתכלות זהירה יותר.

זו לא סיבה להיבהל.
אבל גם לא נכון לתת לכם תשובה אוטומטית ולשלוח אתכם לדרככם.`,
    checklistTitle: "מה כדאי לבדוק עכשיו?",
    checklist: [
      "אילו התחייבויות דורשות טיפול ראשון",
      "האם קיימים פיגורים, החזרות או מגבלות שמשפיעים על האפשרויות",
      "מהו הפער בין שווי הנכס לבין יתרת המשכנתא",
      "אילו אפשרויות עשויות להיות רלוונטיות ואילו עלולות להחמיר את המצב",
    ],
    summary: "במקרה שלכם, השלב הנכון הוא לא עוד מחשבון. הוא שיחה עם אדם שיכול לעבור על הנתונים ולהבין מה עומד מאחוריהם.",
  },
};

const CREDIT_FEEDBACK = {
  clear: "מצוין. היסטוריית תשלומים מסודרת יכולה להשאיר יותר אפשרויות פתוחות לבדיקה.",
  isolated_cases: "תודה על הכנות. כמה מקרים נקודתיים לא מספרים לבדם את כל התמונה.",
  complex: "תודה שאתם משתפים. זה נתון שדורש תשומת לב, אבל עדיין חשוב לבחון אותו יחד עם שאר הנתונים לפני שמסיקים מסקנות.",
};

const WHATSAPP_URL_FALLBACK = "https://chat.whatsapp.com/FtUTJKadSJmE4I0eqXb9KH";

/* ==========================================================================
 * Main component
 * ========================================================================== */
export default function Questionnaire() {
  const { campaignSlug, leadId } = useParams();
  const navigate = useNavigate();

  const [loadingLead, setLoadingLead] = useState(true);
  const [leadContext, setLeadContext] = useState(null); // { name, campaign_id, whatsapp_url }
  const [step, setStep] = useState(STEPS.INTRO);

  // Answers state — mirrors the schema in the spec.
  const [answers, setAnswers] = useState({
    hasProperty: null,
    propertyValue: "",
    mortgageBalance: "",
    monthlyMortgagePayment: "",
    noMortgage: false,
    monthlyLoanPayments: "",
    noOtherLoans: false,
    creditHistory: null, // "clear" | "isolated_cases" | "complex"
    monthlyNetIncome: "",
  });

  // Per-step feedback visibility (so we do NOT auto-advance).
  const [showFeedback, setShowFeedback] = useState({});
  const [errors, setErrors] = useState({});

  // Result state — populated after /submit.
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [loadingTextIdx, setLoadingTextIdx] = useState(0);

  // Consultation state (post-result CTA).
  const [consultationRequested, setConsultationRequested] = useState(false);
  const [consultationLoading, setConsultationLoading] = useState(false);

  /* ------------------------------------------------------------------ Load */
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/questionnaire/lead/${leadId}`);
        setLeadContext(data);
      } catch (e) {
        toast.error("קישור לא תקין. חוזרים לדף הראשי.");
        setTimeout(() => navigate("/"), 1500);
      } finally {
        setLoadingLead(false);
      }
    })();
  }, [leadId, navigate]);

  /* ------------------------------------------------------------- Navigation */
  const stepOrder = useMemo(
    () => buildStepOrder(answers.hasProperty === true),
    [answers.hasProperty]
  );
  const stepIdx = stepOrder.indexOf(step);
  const progressCurrent = Math.max(1, stepIdx); // don't show 0 at intro
  const progressTotal = stepOrder.length - 1; // exclude LOADING from count

  const goNext = () => {
    const i = stepOrder.indexOf(step);
    if (i < 0 || i >= stepOrder.length - 1) return;
    setStep(stepOrder[i + 1]);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const goBack = () => {
    const i = stepOrder.indexOf(step);
    if (i <= 0) return;
    setStep(stepOrder[i - 1]);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  /* ------------------------------------------------------- Loading rotation */
  useEffect(() => {
    if (step !== STEPS.LOADING) return;
    const texts = [
      "בודקים כמה מההכנסה מוקצה להחזרים",
      "מחברים בין הנכס, המשכנתא וההלוואות",
      "מכינים את תמונת המצב הראשונית שלכם",
      "מצאנו כמה דברים שכדאי לראות",
    ];
    setLoadingTextIdx(0);
    const t1 = setTimeout(() => setLoadingTextIdx(1), 600);
    const t2 = setTimeout(() => setLoadingTextIdx(2), 1200);
    const t3 = setTimeout(() => setLoadingTextIdx(3), 1800);
    // Submit + move to result after ~2.2s.
    const t4 = setTimeout(() => submitAndShowResult(), 2200);
    return () => [t1, t2, t3, t4].forEach(clearTimeout);
  }, [step]);

  /* --------------------------------------------------------------- Submit */
  const submitAndShowResult = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      const payloadAnswers = {
        has_property: !!answers.hasProperty,
        property_value: answers.hasProperty ? Number(answers.propertyValue || 0) : 0,
        mortgage_balance: answers.hasProperty && !answers.noMortgage ? Number(answers.mortgageBalance || 0) : 0,
        monthly_mortgage_payment: answers.hasProperty && !answers.noMortgage ? Number(answers.monthlyMortgagePayment || 0) : 0,
        monthly_loan_payments: answers.noOtherLoans ? 0 : Number(answers.monthlyLoanPayments || 0),
        credit_history: answers.creditHistory || "clear",
        monthly_net_income: Number(answers.monthlyNetIncome),
      };
      const { data } = await api.post("/questionnaire/submit", {
        lead_id: leadId,
        campaign_id: leadContext?.campaign_id,
        answers: payloadAnswers,
      });
      setResult(data);
      setStep(STEPS.RESULT);
    } catch (err) {
      const msg = err?.response?.data?.detail || "שגיאה בשמירת הנתונים. אפשר לנסות שוב.";
      toast.error(msg);
      setStep(STEPS.INCOME);
    } finally {
      setSubmitting(false);
    }
  };

  const requestConsultation = async () => {
    if (!result || consultationLoading || consultationRequested) return;
    setConsultationLoading(true);
    try {
      await api.post("/questionnaire/consultation", {
        lead_id: leadId,
        response_id: result.response_id,
      });
      setConsultationRequested(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error("שגיאה בשליחת הבקשה. נסו שוב.");
    } finally {
      setConsultationLoading(false);
    }
  };

  /* --------------------------------------------------------- Live ratio */
  const liveRatio = useMemo(() => {
    const income = Number(answers.monthlyNetIncome || 0);
    if (!income) return null;
    const mortgage = answers.hasProperty && !answers.noMortgage
      ? Number(answers.monthlyMortgagePayment || 0)
      : 0;
    const loans = answers.noOtherLoans ? 0 : Number(answers.monthlyLoanPayments || 0);
    return (((mortgage + loans) / income) * 100);
  }, [answers]);

  /* ============================================================== Render */
  if (loadingLead) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#fafaf9]">
        <div className="w-12 h-12 rounded-full border-4 border-brand-amber border-t-transparent animate-spin" />
      </div>
    );
  }

  return (
    <div
      dir="rtl"
      className="min-h-screen bg-[#fafaf9] text-brand-charcoal"
      data-testid="questionnaire-page"
    >
      {/* Sticky header — matches LandingPage */}
      <header className="sticky top-0 z-40 backdrop-blur-md bg-white/80 border-b border-black/5">
        <div className="max-w-3xl mx-auto px-5 md:px-8 h-16 flex items-center justify-between gap-4">
          <img
            src={BRAND_LOGO}
            alt={leadContext?.brand_name || "אמת או חובות"}
            className="h-10 w-auto"
            data-testid="brand-logo"
          />
          {step !== STEPS.LOADING && step !== STEPS.RESULT && (
            <div className="flex items-center gap-3 flex-1 max-w-xs">
              <span className="text-xs font-semibold text-stone-500 whitespace-nowrap">
                שלב {Math.min(progressCurrent, progressTotal - 1)}/{progressTotal - 1}
              </span>
              <div className="flex-1">
                <ProgressBar current={progressCurrent} total={progressTotal - 1} />
              </div>
            </div>
          )}
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-5 md:px-8 py-8 md:py-12">
        <AnimatePresence mode="wait">
          <motion.div
            key={step}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.35 }}
          >
            {step === STEPS.INTRO && (
              <IntroStep
                brand={leadContext?.brand_name}
                name={leadContext?.name}
                onStart={goNext}
              />
            )}

            {step === STEPS.HAS_PROPERTY && (
              <HasPropertyStep
                value={answers.hasProperty}
                onSelect={(v) => {
                  setAnswers((a) => ({ ...a, hasProperty: v }));
                  setShowFeedback((f) => ({ ...f, hasProperty: true }));
                }}
                showFeedback={showFeedback.hasProperty}
                onBack={goBack}
                onNext={goNext}
              />
            )}

            {step === STEPS.PROPERTY_VALUE && (
              <PropertyValueStep
                value={answers.propertyValue}
                onChange={(v) => setAnswers((a) => ({ ...a, propertyValue: v }))}
                error={errors.propertyValue}
                onBack={goBack}
                onNext={() => {
                  if (!answers.propertyValue || Number(answers.propertyValue) <= 0) {
                    setErrors((e) => ({ ...e, propertyValue: "חסר לנו רק השווי המשוער של הנכס כדי להמשיך." }));
                    return;
                  }
                  setErrors((e) => ({ ...e, propertyValue: null }));
                  setShowFeedback((f) => ({ ...f, propertyValue: true }));
                  setTimeout(goNext, 700);
                }}
                showFeedback={showFeedback.propertyValue}
              />
            )}

            {step === STEPS.MORTGAGE && (
              <MortgageStep
                answers={answers}
                setAnswers={setAnswers}
                errors={errors}
                setErrors={setErrors}
                showFeedback={showFeedback.mortgage}
                markFeedback={() => setShowFeedback((f) => ({ ...f, mortgage: true }))}
                onBack={goBack}
                onNext={goNext}
              />
            )}

            {step === STEPS.OTHER_LOANS && (
              <OtherLoansStep
                answers={answers}
                setAnswers={setAnswers}
                errors={errors}
                setErrors={setErrors}
                showFeedback={showFeedback.otherLoans}
                markFeedback={() => setShowFeedback((f) => ({ ...f, otherLoans: true }))}
                onBack={goBack}
                onNext={goNext}
              />
            )}

            {step === STEPS.CREDIT_HISTORY && (
              <CreditHistoryStep
                value={answers.creditHistory}
                onSelect={(v) => {
                  setAnswers((a) => ({ ...a, creditHistory: v }));
                  setShowFeedback((f) => ({ ...f, creditHistory: true }));
                }}
                showFeedback={showFeedback.creditHistory}
                onBack={goBack}
                onNext={goNext}
              />
            )}

            {step === STEPS.INCOME && (
              <IncomeStep
                answers={answers}
                setAnswers={setAnswers}
                errors={errors}
                setErrors={setErrors}
                ratio={liveRatio}
                onBack={goBack}
                onNext={() => {
                  const income = Number(answers.monthlyNetIncome || 0);
                  if (!income || income <= 0) {
                    setErrors((e) => ({ ...e, income: "הזינו הכנסה חודשית תקינה כדי להמשיך." }));
                    return;
                  }
                  setErrors((e) => ({ ...e, income: null }));
                  goNext();
                }}
              />
            )}

            {step === STEPS.TRANSITION && (
              <TransitionStep onContinue={goNext} />
            )}

            {step === STEPS.LOADING && (
              <LoadingStep idx={loadingTextIdx} />
            )}

            {step === STEPS.RESULT && result && !consultationRequested && (
              <ResultStep
                result={result}
                brand={leadContext?.brand_name}
                whatsappUrl={leadContext?.whatsapp_url || WHATSAPP_URL_FALLBACK}
                onRequestConsultation={requestConsultation}
                consultationLoading={consultationLoading}
              />
            )}

            {step === STEPS.RESULT && result && consultationRequested && (
              <ConsultationThanksStep
                name={leadContext?.name || result.name}
                whatsappUrl={leadContext?.whatsapp_url || WHATSAPP_URL_FALLBACK}
              />
            )}
          </motion.div>
        </AnimatePresence>
      </main>

      {/* Footer — matches LandingPage */}
      <footer className="bg-brand-stone text-stone-300 mt-16">
        <div className="max-w-3xl mx-auto px-5 md:px-8 py-8 flex flex-col md:flex-row items-center justify-between gap-4 text-sm">
          <div className="flex items-center gap-3">
            <img
              src={BRAND_LOGO}
              alt=""
              className="h-8 w-auto opacity-90 invert"
            />
            <span>© 2026 {leadContext?.brand_name || "אמת או חובות"}. כל הזכויות שמורות.</span>
          </div>
          <div className="flex gap-4 flex-wrap justify-center">
            <a href="/privacy" className="hover:text-white" data-testid="footer-privacy-link">
              מדיניות פרטיות
            </a>
            <a href="/terms" className="hover:text-white" data-testid="footer-terms-link">
              תקנון
            </a>
            <a href="/contact" className="hover:text-white" data-testid="footer-contact-link">
              יצירת קשר
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}

/* ==========================================================================
 * Individual step components
 * ========================================================================== */

function IntroStep({ brand, name, onStart }) {
  return (
    <div className="relative">
      <div className="absolute inset-0 amber-halo opacity-60 pointer-events-none -z-10" />
      <div className="space-y-6" data-testid="questionnaire-step-intro">
        <Eyebrow>{brand || "אמת או חובות"}</Eyebrow>
        <h1 className="text-3xl sm:text-4xl md:text-5xl font-[800] leading-[1.15] tracking-tight text-brand-charcoal">
          {name ? `${name}, ` : ""}לפני שמתחילים לעשות סדר, צריך לראות את התמונה
        </h1>
        <p className="text-lg md:text-xl leading-[1.6] text-stone-600">
          המבדק הקצר שלפניכם יעזור לכם להבין כמה מההכנסה החודשית שלכם כבר מופנה למשכנתא, להלוואות ולהחזרים נוספים.
        </p>
        <p className="text-lg leading-[1.6] text-stone-700">
          בסופו תקבלו תמונת מצב ראשונית שתראה:
        </p>
        <ul className="space-y-2 pr-4">
          {[
            "כמה מההכנסה שלכם כבר תפוסה עוד לפני שהחודש מתחיל",
            "האם יש נתונים שכדאי לשים אליהם לב",
            "האם יש טעם להמשיך לבדיקה אישית ומעמיקה יותר",
          ].map((s, i) => (
            <li key={i} className="flex gap-3 text-base leading-relaxed text-brand-charcoal">
              <Check className="w-5 h-5 flex-shrink-0 text-brand-amber mt-1" strokeWidth={3} />
              <span>{s}</span>
            </li>
          ))}
        </ul>
        <p className="leading-relaxed text-stone-500">
          לא צריך ידע פיננסי. לא צריך להכין מסמכים. ובטח שלא לפתוח עכשיו אקסל. רק לענות בכנות על כמה שאלות פשוטות.
        </p>

        <div className="rounded-2xl bg-amber-50/60 border border-amber-200/70 px-4 py-3 text-sm leading-[1.55] text-stone-700">
          <strong className="text-brand-amber">חשוב לדעת:</strong> המבדק אינו ייעוץ פיננסי, בדיקת אשראי או התחייבות
          לקבלת פתרון. התוצאה מבוססת על הנתונים שתזינו ונועדה לתת תמונת מצב ראשונית בלבד.
        </div>

        <div className="space-y-2 pt-2">
          <PrimaryButton onClick={onStart} testId="questionnaire-start-btn" pulse>
            מתחילים מהבסיס
          </PrimaryButton>
          <p className="text-center text-sm text-stone-500">
            כ-2 דקות. בלי התחייבות ובלי לשפוט.
          </p>
        </div>
      </div>
    </div>
  );
}

function HasPropertyStep({ value, onSelect, showFeedback, onBack, onNext }) {
  return (
    <div className="space-y-6" data-testid="questionnaire-step-property">
      <Eyebrow>מתחילים מהבסיס</Eyebrow>
      <h2 className="text-2xl sm:text-3xl md:text-4xl font-[800] tracking-tight text-brand-charcoal leading-[1.2]">
        האם יש בבעלותכם נכס נדל״ן בישראל?
      </h2>
      <div className="space-y-3">
        <ChoiceCard label="כן, יש לנו נכס" selected={value === true} onClick={() => onSelect(true)} testId="q-property-yes" />
        <ChoiceCard label="לא" selected={value === false} onClick={() => onSelect(false)} testId="q-property-no" />
      </div>

      {showFeedback && value === true && (
        <FeedbackBox tone="positive" testId="q-property-feedback-yes">
          מצוין. עכשיו נבדוק איך הנכס משתלב בתמונה הכלכלית המלאה שלכם.
        </FeedbackBox>
      )}
      {showFeedback && value === false && (
        <FeedbackBox tone="neutral" testId="q-property-feedback-no">
          זה בסדר גמור. גם בלי נכס אפשר להבין מה מכביד על התקציב ואיפה כדאי להתחיל לעשות סדר.
        </FeedbackBox>
      )}

      <div className="flex items-center justify-between gap-4 pt-4">
        <BackButton onClick={onBack} />
        <PrimaryButton
          onClick={onNext}
          disabled={value === null || !showFeedback}
          block={false}
          testId="q-property-continue"
        >
          ממשיכים
        </PrimaryButton>
      </div>
    </div>
  );
}

function PropertyValueStep({ value, onChange, error, onBack, onNext, showFeedback }) {
  return (
    <div className="space-y-6" data-testid="questionnaire-step-property-value">
      <Eyebrow>כמה שווה הנכס שלכם היום?</Eyebrow>
      <h2 className="text-2xl sm:text-3xl md:text-4xl font-[800] tracking-tight text-brand-charcoal leading-[1.2]">
        מהו השווי המשוער של הנכס?
      </h2>
      <p className="leading-relaxed text-stone-500">
        לא צריך להזמין שמאי. גם הערכה כללית מספיקה בשלב הזה.
      </p>
      <MoneyInput
        value={value}
        onChange={onChange}
        placeholder="לדוגמה: 2,000,000"
        testId="q-property-value-input"
      />
      {error && (
        <div className="text-sm font-semibold flex items-center gap-2 text-brand-amber">
          <AlertTriangle className="w-4 h-4" /> {error}
        </div>
      )}
      {showFeedback && !error && (
        <FeedbackBox tone="positive" testId="q-property-value-feedback">
          מצוין. הנתון הזה יעזור לנו להבין אילו אפשרויות עשויות להיות רלוונטיות לבדיקה.
        </FeedbackBox>
      )}

      <div className="flex items-center justify-between gap-4 pt-4">
        <BackButton onClick={onBack} />
        <PrimaryButton onClick={onNext} block={false} testId="q-property-value-continue">
          ממשיכים
        </PrimaryButton>
      </div>
    </div>
  );
}

function MortgageStep({ answers, setAnswers, errors, setErrors, showFeedback, markFeedback, onBack, onNext }) {
  const toggleNoMortgage = () => {
    setAnswers((a) => ({
      ...a,
      noMortgage: !a.noMortgage,
      mortgageBalance: !a.noMortgage ? 0 : a.mortgageBalance,
      monthlyMortgagePayment: !a.noMortgage ? 0 : a.monthlyMortgagePayment,
    }));
    setErrors((e) => ({ ...e, mortgage: null }));
  };

  const proceed = () => {
    if (answers.noMortgage) {
      markFeedback();
      setTimeout(onNext, 700);
      return;
    }
    const balance = Number(answers.mortgageBalance || 0);
    const monthly = Number(answers.monthlyMortgagePayment || 0);
    if (balance <= 0 || monthly <= 0) {
      setErrors((e) => ({ ...e, mortgage: "הזינו את יתרת המשכנתא ואת ההחזר החודשי, או סמנו שאין לכם משכנתא." }));
      return;
    }
    setErrors((e) => ({ ...e, mortgage: null }));
    markFeedback();
    setTimeout(onNext, 700);
  };

  return (
    <div className="space-y-6" data-testid="questionnaire-step-mortgage">
      <Eyebrow>ועכשיו, המשכנתא</Eyebrow>
      <h2 className="text-2xl sm:text-3xl md:text-4xl font-[800] tracking-tight text-brand-charcoal leading-[1.2]">
        כמה נשאר לכם לשלם על המשכנתא?
      </h2>
      <p className="leading-relaxed text-stone-500">
        גם כאן, הערכה קרובה מספיקה.
      </p>
      <MoneyInput
        value={answers.mortgageBalance}
        onChange={(v) => setAnswers((a) => ({ ...a, mortgageBalance: v }))}
        placeholder="לדוגמה: 850,000"
        testId="q-mortgage-balance-input"
        disabled={answers.noMortgage}
      />

      <h3 className="text-xl md:text-2xl font-[800] text-brand-charcoal pt-2">
        וכמה אתם מחזירים עליה בכל חודש?
      </h3>
      <MoneyInput
        value={answers.monthlyMortgagePayment}
        onChange={(v) => setAnswers((a) => ({ ...a, monthlyMortgagePayment: v }))}
        placeholder="לדוגמה: 5,200"
        testId="q-mortgage-monthly-input"
        disabled={answers.noMortgage}
      />

      <label className="flex items-center gap-3 cursor-pointer rounded-2xl px-4 py-3 bg-white border border-black/5 hover:bg-stone-50 transition-colors">
        <input
          type="checkbox"
          checked={answers.noMortgage}
          onChange={toggleNoMortgage}
          data-testid="q-no-mortgage-checkbox"
          className="w-5 h-5 accent-brand-amber"
        />
        <span className="font-semibold text-brand-charcoal">אין לנו משכנתא על הנכס</span>
      </label>

      {errors.mortgage && (
        <div className="text-sm font-semibold flex items-center gap-2 text-brand-amber">
          <AlertTriangle className="w-4 h-4" /> {errors.mortgage}
        </div>
      )}
      {showFeedback && !answers.noMortgage && (
        <FeedbackBox tone="attention" testId="q-mortgage-feedback-has">
          תודה. עכשיו מתחילים לראות כמה מההכנסה החודשית כבר תפוסה עוד לפני שהחודש מתחיל.
        </FeedbackBox>
      )}
      {showFeedback && answers.noMortgage && (
        <FeedbackBox tone="positive" testId="q-mortgage-feedback-none">
          מצוין. נכס ללא משכנתא הוא נתון משמעותי בתמונה, אבל נבדוק גם את שאר ההתחייבויות.
        </FeedbackBox>
      )}

      <div className="flex items-center justify-between gap-4 pt-4">
        <BackButton onClick={onBack} />
        <PrimaryButton onClick={proceed} block={false} testId="q-mortgage-continue">
          ממשיכים
        </PrimaryButton>
      </div>
    </div>
  );
}

function OtherLoansStep({ answers, setAnswers, errors, setErrors, showFeedback, markFeedback, onBack, onNext }) {
  const toggleNoLoans = () => {
    setAnswers((a) => ({
      ...a,
      noOtherLoans: !a.noOtherLoans,
      monthlyLoanPayments: !a.noOtherLoans ? 0 : a.monthlyLoanPayments,
    }));
    setErrors((e) => ({ ...e, otherLoans: null }));
  };

  const proceed = () => {
    if (answers.noOtherLoans) {
      markFeedback();
      setTimeout(onNext, 700);
      return;
    }
    const amount = Number(answers.monthlyLoanPayments || 0);
    if (amount <= 0) {
      setErrors((e) => ({ ...e, otherLoans: "הזינו את סכום ההחזרים החודשי, או סמנו שאין לכם הלוואות נוספות." }));
      return;
    }
    setErrors((e) => ({ ...e, otherLoans: null }));
    markFeedback();
    setTimeout(onNext, 700);
  };

  return (
    <div className="space-y-6" data-testid="questionnaire-step-loans">
      <Eyebrow>ומה קורה מחוץ למשכנתא?</Eyebrow>
      <h2 className="text-2xl sm:text-3xl md:text-4xl font-[800] tracking-tight text-brand-charcoal leading-[1.2]">
        כמה אתם מחזירים בכל חודש על הלוואות נוספות?
      </h2>
      <p className="leading-relaxed text-stone-500">
        כולל הלוואות מהבנק, מחברות אשראי, לרכב או לכל מטרה אחרת.
      </p>
      <MoneyInput
        value={answers.monthlyLoanPayments}
        onChange={(v) => setAnswers((a) => ({ ...a, monthlyLoanPayments: v }))}
        placeholder="לדוגמה: 3,500"
        testId="q-loans-monthly-input"
        disabled={answers.noOtherLoans}
      />
      <label className="flex items-center gap-3 cursor-pointer rounded-2xl px-4 py-3 bg-white border border-black/5 hover:bg-stone-50 transition-colors">
        <input
          type="checkbox"
          checked={answers.noOtherLoans}
          onChange={toggleNoLoans}
          data-testid="q-no-loans-checkbox"
          className="w-5 h-5 accent-brand-amber"
        />
        <span className="font-semibold text-brand-charcoal">אין לנו הלוואות נוספות</span>
      </label>

      {errors.otherLoans && (
        <div className="text-sm font-semibold flex items-center gap-2 text-brand-amber">
          <AlertTriangle className="w-4 h-4" /> {errors.otherLoans}
        </div>
      )}
      {showFeedback && !answers.noOtherLoans && (
        <FeedbackBox tone="attention" testId="q-loans-feedback-has">
          רשמנו. בהמשך נחבר את הסכום הזה להכנסה החודשית ונראה כמה מקום הוא תופס בפועל.
        </FeedbackBox>
      )}
      {showFeedback && answers.noOtherLoans && (
        <FeedbackBox tone="positive" testId="q-loans-feedback-none">
          מצוין. לפחות מהצד הזה, אין כרגע החזרים נוספים שמכבידים על החודש.
        </FeedbackBox>
      )}

      <div className="flex items-center justify-between gap-4 pt-4">
        <BackButton onClick={onBack} />
        <PrimaryButton onClick={proceed} block={false} testId="q-loans-continue">
          ממשיכים
        </PrimaryButton>
      </div>
    </div>
  );
}

function CreditHistoryStep({ value, onSelect, showFeedback, onBack, onNext }) {
  return (
    <div className="space-y-6" data-testid="questionnaire-step-credit">
      <Eyebrow>האם היו תקלות בדרך?</Eyebrow>
      <h2 className="text-2xl sm:text-3xl md:text-4xl font-[800] tracking-tight text-brand-charcoal leading-[1.2]">
        האם במהלך שלוש השנים האחרונות חזרו לכם צ׳קים, הוראות קבע או תשלומים?
      </h2>
      <div className="space-y-3">
        <ChoiceCard label="לא, הכול שולם כסדרו" selected={value === "clear"} onClick={() => onSelect("clear")} testId="q-credit-clear" />
        <ChoiceCard label="כן, היו כמה מקרים נקודתיים" selected={value === "isolated_cases"} onClick={() => onSelect("isolated_cases")} testId="q-credit-isolated" />
        <ChoiceCard label="כן, המצב מורכב יותר" selected={value === "complex"} onClick={() => onSelect("complex")} testId="q-credit-complex" />
      </div>

      {showFeedback && value && (
        <FeedbackBox tone={value === "clear" ? "positive" : "attention"} testId="q-credit-feedback">
          {CREDIT_FEEDBACK[value]}
        </FeedbackBox>
      )}

      <div className="flex items-center justify-between gap-4 pt-4">
        <BackButton onClick={onBack} />
        <PrimaryButton onClick={onNext} disabled={!value || !showFeedback} block={false} testId="q-credit-continue">
          ממשיכים
        </PrimaryButton>
      </div>
    </div>
  );
}

function IncomeStep({ answers, setAnswers, errors, ratio, onBack, onNext }) {
  const feedback = useMemo(() => {
    if (ratio === null || !answers.monthlyNetIncome) return null;
    if (ratio <= 25) return {
      tone: "positive",
      text: "לפי הנתונים שמילאתם, נראה שנשאר לכם מרחב נשימה יחסי. נשאר לבדוק אם מבנה ההחזרים שלכם יכול להיות יעיל יותר.",
    };
    if (ratio <= 35) return {
      tone: "attention",
      text: "מתחילה להצטייר תמונה. חלק משמעותי מההכנסה שלכם כבר מוקצה להחזרים עוד לפני שהחודש מתחיל.",
    };
    if (ratio <= 45) return {
      tone: "attention",
      text: "כדאי לשים לב: נתח גדול מההכנסה שלכם כבר מופנה להחזרים. במצב כזה, גם משכורת טובה יכולה להרגיש כאילו היא נעלמת מהר מדי.",
    };
    return {
      tone: "attention",
      text: "הנתון הזה מצדיק בדיקה אישית יותר. אין צורך להיבהל, אבל גם לא כדאי להסתפק במספר שמופיע על המסך.",
    };
  }, [ratio, answers.monthlyNetIncome]);

  return (
    <div className="space-y-6" data-testid="questionnaire-step-income">
      <Eyebrow>וכמה נכנס הביתה בכל חודש?</Eyebrow>
      <h2 className="text-2xl sm:text-3xl md:text-4xl font-[800] tracking-tight text-brand-charcoal leading-[1.2]">
        מהי ההכנסה החודשית נטו של משק הבית?
      </h2>
      <p className="leading-relaxed text-stone-500">
        כל המשכורות וההכנסות הקבועות שנכנסות בפועל, אחרי מסים וניכויים.
      </p>
      <MoneyInput
        value={answers.monthlyNetIncome}
        onChange={(v) => setAnswers((a) => ({ ...a, monthlyNetIncome: v }))}
        placeholder="לדוגמה: 24,000"
        testId="q-income-input"
      />
      <p className="text-sm leading-relaxed text-stone-500">
        אם ההכנסה משתנה מחודש לחודש, הזינו ממוצע של שלושת החודשים האחרונים.
      </p>

      {errors.income && (
        <div className="text-sm font-semibold flex items-center gap-2 text-brand-amber">
          <AlertTriangle className="w-4 h-4" /> {errors.income}
        </div>
      )}

      {feedback && (
        <FeedbackBox tone={feedback.tone} testId="q-income-feedback">
          {feedback.text}
        </FeedbackBox>
      )}

      <div className="flex items-center justify-between gap-4 pt-4">
        <BackButton onClick={onBack} />
        <PrimaryButton
          onClick={onNext}
          disabled={!answers.monthlyNetIncome}
          block={false}
          testId="q-income-continue"
        >
          ממשיכים לתמונה שלי
        </PrimaryButton>
      </div>
    </div>
  );
}

function TransitionStep({ onContinue }) {
  return (
    <div className="relative">
      <div className="absolute inset-0 amber-halo opacity-60 pointer-events-none -z-10" />
      <div className="space-y-6 text-center py-8" data-testid="questionnaire-step-transition">
        <h2 className="text-3xl sm:text-4xl md:text-5xl font-[800] tracking-tight text-brand-charcoal leading-[1.15]">
          זהו. המספרים כבר מתחילים להתחבר
        </h2>
        <p className="text-lg md:text-xl leading-[1.6] text-stone-600">
          על סמך התשובות שלכם הכנו תמונת מצב ראשונית.
        </p>
        <div className="pt-4 max-w-md mx-auto">
          <PrimaryButton onClick={onContinue} testId="questionnaire-transition-continue" pulse>
            לתמונה שלי
          </PrimaryButton>
        </div>
      </div>
    </div>
  );
}

function LoadingStep({ idx }) {
  const texts = [
    "בודקים כמה מההכנסה מוקצה להחזרים",
    "מחברים בין הנכס, המשכנתא וההלוואות",
    "מכינים את תמונת המצב הראשונית שלכם",
    "מצאנו כמה דברים שכדאי לראות",
  ];
  return (
    <div className="min-h-[50vh] flex flex-col items-center justify-center gap-6 text-center" data-testid="questionnaire-step-loading">
      <div className="w-14 h-14 rounded-full border-4 border-brand-amber border-t-transparent animate-spin" />
      <h3 className="text-2xl font-[800] text-brand-charcoal">רגע, מחברים בין המספרים...</h3>
      <AnimatePresence mode="wait">
        <motion.p
          key={idx}
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -6 }}
          transition={{ duration: 0.35 }}
          className="text-lg text-stone-500"
        >
          {texts[idx]}
        </motion.p>
      </AnimatePresence>
    </div>
  );
}

function ResultStep({ result, brand, whatsappUrl, onRequestConsultation, consultationLoading }) {
  const tier = result.result_tier;
  const content = RESULT_CONTENT[tier] || RESULT_CONTENT.tight;
  const ratioDisplay = Math.round(result.payment_to_income_ratio || 0);
  const total = result.total_monthly_payments;
  const equity = result.estimated_property_equity;

  return (
    <div className="space-y-8 pb-12 relative" data-testid="questionnaire-step-result">
      <div className="absolute inset-0 top-0 h-64 amber-halo opacity-60 pointer-events-none -z-10" />
      <div>
        <Eyebrow>{brand || "אמת או חובות"}</Eyebrow>
        <h1 className="text-3xl sm:text-4xl md:text-5xl font-[800] tracking-tight text-brand-charcoal leading-[1.15] mt-3">
          זו תמונת המצב הראשונית שלכם
        </h1>
        <p className="text-lg md:text-xl leading-[1.6] text-stone-600 mt-3">
          חיברנו בין ההכנסה, המשכנתא, ההלוואות והנתונים הנוספים שמילאתם. הנה מה שעולה מהתמונה כרגע.
        </p>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-testid="questionnaire-kpi-cards">
        <KpiCard label="כמה נכנס בכל חודש" value={result.monthly_net_income} testId="kpi-income" suffix="₪" />
        <KpiCard label="כמה יוצא על החזרים" value={total} testId="kpi-payments" suffix="₪" />
        <KpiCard label="כמה מההכנסה כבר תפוס" value={ratioDisplay} testId="kpi-ratio" suffix="%" highlight />
      </div>

      <p className="text-sm leading-relaxed text-stone-500">
        החישוב אינו כולל הוצאות מחיה, מסגרות אשראי, מינוס, מזונות, תשלומים משתנים או התחייבויות שלא הוזנו בשאלון.
      </p>

      {/* Tier-based narrative */}
      <div className="space-y-4 bg-white rounded-3xl shadow-[0_12px_40px_rgba(28,25,23,0.08)] border border-black/5 p-6 md:p-8">
        <h2 className="text-2xl sm:text-3xl font-[800] tracking-tight text-brand-charcoal leading-[1.2]">
          {content.title}
        </h2>
        {content.body.split("\n\n").map((para, i) => (
          <p key={i} className="text-base leading-[1.7] whitespace-pre-line text-stone-700">{para}</p>
        ))}

        <h3 className="text-xl font-[800] text-brand-charcoal pt-3">{content.checklistTitle}</h3>
        <ul className="space-y-2 pr-2">
          {content.checklist.map((item, i) => (
            <li key={i} className="flex gap-3 text-base leading-relaxed text-stone-700">
              <Check className="w-5 h-5 flex-shrink-0 text-brand-amber mt-1" strokeWidth={3} />
              <span>{item}</span>
            </li>
          ))}
        </ul>

        <FeedbackBox tone="attention" testId="q-result-summary">
          {content.summary}
        </FeedbackBox>
      </div>

      {/* Optional equity note */}
      {equity !== null && equity !== undefined && (
        <EquityNote equity={equity} />
      )}

      {/* Actions */}
      <div className="space-y-6 pt-2">
        <h2 className="text-2xl sm:text-3xl font-[800] tracking-tight text-brand-charcoal">
          ומה עושים מכאן?
        </h2>
        <p className="leading-[1.6] text-stone-700">
          אתם לא חייבים לקבל עכשיו החלטה גדולה. אפשר לבחור את הצעד שמתאים לכם.
        </p>

        {/* Primary — consultation */}
        <div className="bg-white rounded-3xl shadow-[0_12px_40px_rgba(28,25,23,0.08)] border-2 border-brand-amber p-6 md:p-8">
          <h3 className="text-xl md:text-2xl font-[800] text-brand-charcoal">
            רוצים להבין מה עומד מאחורי המספרים?
          </h3>
          <p className="mt-2 leading-[1.6] text-stone-700">
            בשיחה קצרה עם מומחה של קהילת {`"${brand || "אמת או חובות"}"`} תוכלו לעבור על התמונה הראשונית,
            לשאול שאלות ולהבין האם יש טעם להמשיך לבדיקה מעמיקה יותר.
          </p>
          <p className="mt-2 leading-relaxed text-stone-500">
            השיחה אינה כרוכה בתשלום או בהתחייבות להמשך.
          </p>
          <div className="mt-4">
            <PrimaryButton
              onClick={onRequestConsultation}
              disabled={consultationLoading}
              testId="q-consultation-btn"
              pulse
            >
              <PhoneCall className="inline-block ml-2 w-5 h-5" />
              {consultationLoading ? "שולחים..." : "אני רוצה שיחת ייעוץ ללא עלות"}
            </PrimaryButton>
            <p className="text-center text-sm mt-2 text-stone-500">
              נחזור אליכם לתיאום בזמן שנוח לכם.
            </p>
          </div>
        </div>

        {/* Secondary — WhatsApp */}
        <div className="bg-white rounded-3xl shadow-[0_12px_40px_rgba(28,25,23,0.08)] border border-black/5 p-6 md:p-8">
          <h3 className="text-xl md:text-2xl font-[800] text-brand-charcoal">
            מעדיפים להתחיל בקצב שלכם?
          </h3>
          <p className="mt-2 leading-[1.6] text-stone-700">
            בקבוצת הוואטסאפ של {`"${brand || "אמת או חובות"}"`} מחכים לכם תכנים, הסברים וכלים שיעזרו לכם להבין
            טוב יותר איך הכסף שלכם עובד.
          </p>
          <div className="mt-4">
            <a
              href={whatsappUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="q-whatsapp-btn"
              className="inline-flex items-center justify-center w-full rounded-full px-8 h-14 text-base font-bold transition-colors bg-white border-2 border-brand-charcoal text-brand-charcoal hover:bg-stone-50 active:scale-95"
            >
              <MessageCircle className="inline-block ml-2 w-5 h-5" />
              מצטרפים לקבוצת הוואטסאפ
            </a>
            <p className="text-center text-sm mt-2 text-stone-500">
              ההצטרפות ללא עלות. אפשר לצאת בכל רגע.
            </p>
          </div>
        </div>
      </div>

      {/* Closing */}
      <div className="pt-6 text-center">
        <h3 className="text-2xl sm:text-3xl font-[800] text-brand-charcoal">
          אתם לא חייבים לפתור הכול היום
        </h3>
        <p className="mt-3 leading-[1.6] text-stone-600 max-w-xl mx-auto">
          אבל עכשיו אתם כבר רואים קצת יותר ברור איפה אתם עומדים ומה כדאי לבדוק.
          וזה בדיוק המקום שבו מתחיל סדר.
        </p>
      </div>
    </div>
  );
}

function KpiCard({ label, value, suffix = "", testId, highlight = false }) {
  const display = typeof value === "number" ? value.toLocaleString("he-IL") : (value || "—");
  return (
    <div
      className={`rounded-3xl p-5 text-center bg-white border transition-colors ${
        highlight
          ? "border-brand-amber shadow-[0_4px_20px_rgba(217,119,6,0.14)]"
          : "border-black/5 shadow-[0_4px_16px_rgba(28,25,23,0.04)]"
      }`}
      data-testid={testId}
    >
      <div className="text-sm font-semibold text-stone-500">{label}</div>
      <div className={`mt-2 text-3xl md:text-4xl font-[800] ${highlight ? "text-brand-amber" : "text-brand-charcoal"}`}>
        {display}{suffix ? ` ${suffix}` : ""}
      </div>
    </div>
  );
}

function EquityNote({ equity }) {
  const positive = equity > 0;
  return (
    <div
      className={`rounded-2xl px-5 py-4 text-base leading-relaxed border ${
        positive
          ? "bg-stone-100 border-stone-200/60 text-stone-800"
          : "bg-amber-50 border-amber-200/60 text-amber-900"
      }`}
      data-testid="q-equity-note"
    >
      {positive
        ? "לפי ההערכה שמילאתם, נראה שקיים פער בין שווי הנכס לבין יתרת המשכנתא. הפער הזה עשוי להיות רלוונטי לבדיקה אישית."
        : "לפי הנתונים שהוזנו, יתרת המשכנתא קרובה לשווי הנכס או גבוהה ממנו. כדאי לוודא שההערכות מדויקות ולבחון את הנתון באופן אישי."}
    </div>
  );
}

function ConsultationThanksStep({ name, whatsappUrl }) {
  return (
    <div className="relative">
      <div className="absolute inset-0 amber-halo opacity-60 pointer-events-none -z-10" />
      <div className="space-y-6 py-8 text-center" data-testid="questionnaire-step-consultation-thanks">
        <div className="mx-auto w-16 h-16 rounded-full flex items-center justify-center bg-amber-50 border border-amber-200/70">
          <Check className="w-8 h-8 text-brand-amber" strokeWidth={3} />
        </div>
        <h2 className="text-3xl sm:text-4xl md:text-5xl font-[800] tracking-tight text-brand-charcoal leading-[1.15]">
          הבקשה שלכם התקבלה
        </h2>
        <p className="text-lg md:text-xl leading-[1.6] text-stone-600">
          תודה{name ? `, ${name}` : ""}.
        </p>
        <p className="text-lg leading-[1.6] text-stone-700 max-w-xl mx-auto">
          אחד ממומחי קהילת {`"אמת או חובות"`} יחזור אליכם כדי לתאם שיחת ייעוץ ללא עלות.
          עד אז, אתם מוזמנים להצטרף לקבוצת הוואטסאפ ולקבל תכנים שיעזרו לכם להמשיך לעשות סדר.
        </p>

        <div className="pt-4 max-w-md mx-auto">
          <a
            href={whatsappUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="q-thanks-whatsapp-btn"
            className="inline-flex items-center justify-center w-full rounded-full px-8 h-14 text-base font-bold text-white bg-brand-amber hover:bg-brand-amberHover pulse-cta transition-all active:scale-95"
          >
            <MessageCircle className="inline-block ml-2 w-5 h-5" />
            מצטרפים לקבוצת הוואטסאפ
          </a>
          <p className="text-sm mt-3 text-stone-500">
            בלי ספאם, בלי מילים של בנקאים ובלי להיכנס לכם לצלחת.
          </p>
        </div>
      </div>
    </div>
  );
}
